import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import { ulid } from 'ulid';
import { useAuthStore } from '@/stores/auth';
import { normalizeError } from '@/utils/error';
import type { ApiEnvelope } from '@/types/api';

let isLoggingOut = false;

function handleGlobal401(url: string | undefined): void {
  if (url?.includes('/auth/login')) return;
  if (isLoggingOut) return;
  isLoggingOut = true;

  const { logout } = useAuthStore.getState();
  logout();
  window.location.replace('/login');

  setTimeout(() => { isLoggingOut = false; }, 2000);
}

function setupInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    config.headers = config.headers ?? {};
    if (!config.headers['X-Trace-Id']) {
      config.headers['X-Trace-Id'] = ulid();
    }
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (!config.headers.Accept) {
      config.headers.Accept = 'application/json';
    }
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse<ApiEnvelope<unknown>>) => {
      if (response.config.responseType === 'blob') {
        return response.data as never;
      }
      return response.data?.data as never;
    },
    (error) => {
      if (error?.response?.status === 401) {
        handleGlobal401(error.config?.url);
      }
      return Promise.reject(normalizeError(error));
    },
  );
}

/**
 * V1 API client — Phase 1/2 全部端点。
 *
 * baseURL=/api/v1：开发走 Vite proxy 转发到后端 8000；生产同源部署也直连。
 *
 * 响应拦截器把 D-106 信封 `{ success, code, message, data, trace_id }`
 * 直接剥成 data 字段给调用方；失败统一抛 AppError。
 */
export const apiClient = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
});

/**
 * V2 API client — Phase 3 新端点（自动调度 / 移动端 / GPS）。
 */
export const apiClientV2 = axios.create({
  baseURL: '/api/v2',
  timeout: 30_000,
});

setupInterceptors(apiClient);
setupInterceptors(apiClientV2);
